import { CreditCardIcon, ListUnorderedIcon } from "@primer/octicons-react";
import { Box, Link } from "@primer/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import HocSinhShortInfoNew from "../../../components/hoc-sinh/HocSinhShortInfoNew";
import Button from "../../../components/ui/button";
import DataTable from "../../../components/ui/data-table";
import TextWithEllipse from "../../../components/ui/text-with-ellipse";
import { useCommonContext } from "../../../contexts/common";
import { thuPhiTabAction } from "../../../state/actions/thu-phi-tab/thuPhiTabAction";
import { RootState } from "../../../state/reducers";
import CongNoModal from "./CongNoModal";

interface DanhSachHocSinhPageProps {
  dataSourceFilter: any[];
  isLoading: boolean;
  onReload: () => void;
  truongFilter: number;
  khoiFilter: number;
  heFilter: number;
  onFilterChange: {
    setTruongFilter: (value: number) => void;
    setKhoiFilter: (value: number) => void;
    setHeFilter: (value: number) => void;
  };
}

const DanhSachHocSinhPage = ({
  dataSourceFilter,
  isLoading,
  onReload,
  truongFilter,
  khoiFilter,
  heFilter,
  onFilterChange
}: DanhSachHocSinhPageProps) => {
  const { translate } = useCommonContext();
  const dispatch = useDispatch();
  const { nam_hoc } = useSelector((x: RootState) => x.common);
  const { tabs } = useSelector((x: RootState) => x.thuPhiTab);
  const pendingHocSinhDataRef = useRef<{ data: any; timestamp: number } | null>(null);
  const [congNoModalOpen, setCongNoModalOpen] = useState(false);
  const [selectedHocSinh, setSelectedHocSinh] = useState<{ id: number; ho_ten?: string } | null>(null);

  useEffect(() => {
    if (pendingHocSinhDataRef.current && tabs.length > 0) {
      const newTab = tabs.find((t) => t.title === "Untitled" && !t.hocSinh);
      
      if (newTab) {
        dispatch(thuPhiTabAction.CHANGEG_SELECTED_TAB(newTab.id));
      }
    }
  }, [tabs, dispatch]);

  useEffect(() => {
    if (pendingHocSinhDataRef.current && tabs.length > 0) {
      const newTab = tabs.find((t) => t.title === "Untitled" && !t.hocSinh);
      
      if (newTab && pendingHocSinhDataRef.current.data) {
        const { data } = pendingHocSinhDataRef.current;
        
        const hocSinhData = {
          id: data.ts_hocsinh_id || data.id || 0,
          ma_hs: data.ma_hs || "",
          ho_ten: data.ho_ten || "",
          dm_gioitinh_id: data.dm_gioitinh_id || 0,
          ngay_sinh: data.ngay_sinh || null,
          nam_hoc: data.nam_hoc || nam_hoc,
          ten_truong: data.ten_truong || "",
          ten_truong_en: data.ten_truong_en || "",
          ten_khoi: data.ten_khoi || "",
          ten_khoi_en: data.ten_khoi_en || "",
          ten_he: data.ten_he || "",
          ten_he_en: data.ten_he_en || "",
          ten_lop: data.ten_lop || "",
          ...data
        };
        
        dispatch(thuPhiTabAction.UPDATE_TAB_DATA({
          ...newTab,
          title: data.ho_ten || "Untitled",
          hocSinh: hocSinhData,
          request: {
            nam_hoc: nam_hoc,
            ts_hocsinh_id: hocSinhData.id
          }
        }));
        
        pendingHocSinhDataRef.current = null;
      }
    }
  }, [tabs, dispatch, nam_hoc]);

  const handleNopPhi = useCallback((data: any) => {
    const hocSinhId = data.ts_hocsinh_id || data.id || 0;
        const existingTab = tabs.find((tab) => 
      tab.hocSinh && (tab.hocSinh.id === hocSinhId || tab.hocSinh.ts_hocsinh_id === hocSinhId)
    );
    if (existingTab) {
      dispatch(thuPhiTabAction.CHANGEG_SELECTED_TAB(existingTab.id));
      return;
    }
    pendingHocSinhDataRef.current = {
      data: data,
      timestamp: Date.now()
    };
        dispatch(thuPhiTabAction.ADD_NEW_TAB(undefined));
      }, [dispatch, tabs]);

  const handleEditClick = useCallback((data: any) => {
    console.log("Edit", data);
  }, []);

  const columns = useMemo(() => {
    const quanHes = [
      {
        id: 2,
        text: "Bố",
      },
      {
        id: 1,
        text: "Mẹ",
      },
      {
        id: 3,
        text: "Giám hộ",
      },
    ];
    return [
      {
        id: "cmd",
        width: "100px",
        caption: translate("Thao tác"),
        align: "center" as const,
        fixed: true,
        cellRender: (data: any) => {
          return (
            <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
              <Button
                variant="primary"
                size="small"
                leadingVisual={CreditCardIcon}
                onClick={() => handleNopPhi(data)}
                aria-label={translate("Nộp phí")}
              />
              <Button
                variant="default"
                size="small"
                onClick={() => {
                  setSelectedHocSinh({ id: data.ts_hocsinh_id, ho_ten: data.ho_ten });
                  setCongNoModalOpen(true);
                }}
                aria-label={translate("Công nợ")}
                leadingVisual={ListUnorderedIcon}
              />
            </Box>
          );
        },
      },
      {
        dataField: "trang_thai",
        width: "150px",
        caption: translate("Trạng thái"),
        fixed: true,
        isMainColumn: true,
        cellRender: (data: any) => {
          let color: any = "fg.default";
          switch (data.trang_thai) {
            case "New Enrollment":
              color = "accent.emphasis";
              break;
            case "Thôi học":
              color = "danger.emphasis";
              break;
            case "Học sinh lên lớp chưa đóng phí":
              color = "attention.emphasis";
              break;
            default:
              break;
          }
          return (
            <Box
              sx={{
                color: color,
              }}
            >
              <TextWithEllipse text={data.trang_thai || ""} lineNumber={1} />
            </Box>
          );
        },
      },
      {
        dataField: "ma_hs",
        width: "100px",

        caption: translate("Mã HS"),
        fixed: true,
        cellRender: (data: any) => {
          return (
            <RouterLink
              to={`../../hoc-sinh/${data.ts_hocsinh_id}`}
              style={{ color: "blue", fontWeight: 600, textDecoration: "none" }}
            >
              {data.ma_hs}
            </RouterLink>
          );
        },
      },
      {
        dataField: "ho_ten",
        width: "200px",
        caption: translate("Học sinh"),
        cellRender: (data: any) => {
          return <HocSinhShortInfoNew data={data} onClick={handleEditClick} />;
        },
      },
      {
        dataField: "ten_lop",
        caption: translate("Trường lớp"),
        width: "250px",
        cellRender: (data: any) => {
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "baseline",
              }}
            >
              <Box sx={{ display: "flex" }}>
                <Box sx={{ fontWeight: 600 }}>{data.ten_lop || ""}</Box>
              </Box>
              <Box sx={{ color: "fg.muted" }}>
                {data.ten_khoi || ""}, Hệ {data.ten_he || ""}
              </Box>
            </Box>
          );
        },
      },
      ...quanHes.map((qh: any) => ({
        dataField: `HT_${qh.id}`,
        width: "200px",
        caption: translate(`Thông tin ${qh.text}`),
        cellRender: (data: any) => {
          const handleRowClick = () => {
          };
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "baseline",
                whiteSpace: "nowrap",
              }}
            >
              <Box sx={{ display: "flex" }} onClick={handleRowClick}>
                <Link href="#" sx={{ textDecoration: "none" }}>
                  <Box sx={{ fontWeight: 600 }}>
                    {data[`HT_${qh.id}`] || ""}
                  </Box>
                </Link>
              </Box>
              <Box sx={{ color: "fg.muted" }}>
                {(() => {
                  const dt = data[`DT_${qh.id}`] || "";
                  const em = data[`EM_${qh.id}`] || "";
                  const text =
                    dt && em ? `${dt} - ${em}` : dt || em || "";
                  return (
                <TextWithEllipse
                    text={text}
                    lineNumber={1}
                  />
                  );
                })()}
              </Box>
            </Box>
          );
        },
      })),
    ];
  }, [translate, handleNopPhi, handleEditClick]);
  return (
    <React.Fragment>
      <Box sx={{ p: 2 }}>
        <Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ pt: 2 }}>
              <DataTable
                data={dataSourceFilter}
                columns={columns}
                height={`${window.innerHeight - 240}px`}
                isLoading={isLoading}
                filterRow={{
                  enable: true,
                }}
                searchEnable={true}
                exportEnable={true}
                paging={{
                  enable: true,
                  pageSize: 50,
                  pageSizeItems: [50, 100, 200, 500, 1000],
                }}
                actionComponent={
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    {/* <ComboboxTruong
                      value={truongFilter}
                      onValueChanged={(e) => {
                        setTruongFilter(e);
                        setKhoiFilter(0);
                        setHeFilter(0);
                      }}
                      isShowClearButton={true}
                      size="small"
                    />
                    <ComboboxKhoi
                      dm_truong_id={truongFilter}
                      value={khoiFilter}
                      width={110}
                      onValueChanged={(e) => {
                        setKhoiFilter(e);
                        setHeFilter(0);
                      }}
                      isShowClearButton={true}
                      size="small"
                    />
                    <ComboboxHe
                      dm_khoi_id={khoiFilter}
                      dm_truong_id={truongFilter}
                      value={heFilter}
                      onValueChanged={(e) => {
                        setHeFilter(e);
                      }}
                      isShowClearButton={true}
                      size="small"
                    /> */}
                    <Button
                      variant="default"
                      size="small"
                      onClick={onReload}
                    >
                      {translate("Refresh")}
                    </Button>
                  </Box>
                }
              />
            </Box>
          </Box>
        </Box>
      </Box>
      {selectedHocSinh && (
        <CongNoModal
          isOpen={congNoModalOpen}
          onClose={() => {
            setCongNoModalOpen(false);
            setSelectedHocSinh(null);
          }}
          ts_hocsinh_id={selectedHocSinh.id}
          nam_hoc={nam_hoc}
          ho_ten={selectedHocSinh.ho_ten}
        />
      )}
    </React.Fragment>
  );
};
export default DanhSachHocSinhPage;
